import { useTranslation } from 'react-i18next';

import { cn } from '@/shared/lib/utils';
import { DateBox, Input, Panel } from '@/shared/ui';
import { ToggleGroup, ToggleGroupItem } from '@/shared/ui/shadcn';

import type { TermRecurrence, TermSpan } from '../termGenerator';

/** Dny v týdnu v pořadí Po–Ne; hodnota = getDay() (Po=1 … Ne=0). */
const DOW: ReadonlyArray<{ value: number; labelKey: string }> = [
  { value: 1, labelKey: 'mon' },
  { value: 2, labelKey: 'tue' },
  { value: 3, labelKey: 'wed' },
  { value: 4, labelKey: 'thu' },
  { value: 5, labelKey: 'fri' },
  { value: 6, labelKey: 'sat' },
  { value: 0, labelKey: 'sun' },
];

export interface RecurrenceSettingsProps {
  span: TermSpan;
  onSpanChange: (value: TermSpan) => void;
  from: string;
  onFromChange: (value: string) => void;
  to: string;
  onToChange: (value: string) => void;
  recurrence: TermRecurrence;
  onRecurrenceChange: (value: TermRecurrence) => void;
  everyDays: number;
  onEveryDaysChange: (value: number) => void;
  everyWeeks: number;
  onEveryWeeksChange: (value: number) => void;
  daysOfWeek: number[];
  onDaysOfWeekChange: (value: number[]) => void;
}

export function RecurrenceSettings({
  span,
  onSpanChange,
  from,
  onFromChange,
  to,
  onToChange,
  recurrence,
  onRecurrenceChange,
  everyDays,
  onEveryDaysChange,
  everyWeeks,
  onEveryWeeksChange,
  daysOfWeek,
  onDaysOfWeekChange,
}: RecurrenceSettingsProps) {
  const { t } = useTranslation('venua-planner');

  return (
    <>
      <Panel variant="outline" title={t('serieDetail.generateTerms.span.title')}>
        <div className="gap-phx-3 flex flex-col">
          <ToggleGroup
            type="single"
            variant="outline"
            size="sm"
            value={span}
            onValueChange={(value) => value && onSpanChange(value as TermSpan)}
            className="w-full"
          >
            <ToggleGroupItem value="includeEnds" className="flex-1">
              {t('serieDetail.generateTerms.span.includeEnds')}
            </ToggleGroupItem>
            <ToggleGroupItem value="startsOnly" className="flex-1">
              {t('serieDetail.generateTerms.span.startsOnly')}
            </ToggleGroupItem>
          </ToggleGroup>
          <div className="gap-phx-2 flex items-center">
            <DateBox
              size="sm"
              value={from}
              onChange={onFromChange}
              fieldWidth={150}
              data-testid="generate-from"
            />
            <span className="text-phx-text-muted">–</span>
            <DateBox
              size="sm"
              value={to}
              onChange={onToChange}
              fieldWidth={150}
              data-testid="generate-to"
            />
          </div>
        </div>
      </Panel>

      <Panel variant="outline" title={t('serieDetail.generateTerms.recurrence.title')}>
        <div className="gap-phx-3 flex flex-col">
          <ToggleGroup
            type="single"
            variant="outline"
            size="sm"
            value={recurrence}
            onValueChange={(value) => value && onRecurrenceChange(value as TermRecurrence)}
            className="w-full"
          >
            <ToggleGroupItem value="chain" className="flex-1">
              {t('serieDetail.generateTerms.recurrence.chain')}
            </ToggleGroupItem>
            <ToggleGroupItem value="days" className="flex-1">
              {t('serieDetail.generateTerms.recurrence.days')}
            </ToggleGroupItem>
            <ToggleGroupItem value="weeks" className="flex-1">
              {t('serieDetail.generateTerms.recurrence.weeks')}
            </ToggleGroupItem>
          </ToggleGroup>

          {recurrence === 'chain' ? (
            <p className="text-phx-text-muted text-phx-sm">
              {t('serieDetail.generateTerms.recurrence.chainHint')}
            </p>
          ) : null}

          {recurrence === 'days' ? (
            <div className="gap-phx-2 text-phx-md flex items-center">
              <span>{t('serieDetail.generateTerms.recurrence.everyPrefix')}</span>
              <Input
                type="number"
                size="sm"
                min={1}
                max={99}
                value={everyDays}
                onChange={(event) =>
                  onEveryDaysChange(Math.max(1, Number(event.target.value) || 1))
                }
                className="w-[46px] text-center"
              />
              <span>{t('serieDetail.generateTerms.recurrence.daysSuffix')}</span>
            </div>
          ) : null}

          {recurrence === 'weeks' ? (
            <div className="gap-phx-3 flex flex-col">
              <div className="gap-phx-2 text-phx-md flex items-center">
                <span>{t('serieDetail.generateTerms.recurrence.everyPrefix')}</span>
                <Input
                  type="number"
                  size="sm"
                  min={1}
                  max={99}
                  value={everyWeeks}
                  onChange={(event) =>
                    onEveryWeeksChange(Math.max(1, Number(event.target.value) || 1))
                  }
                  className="w-[46px] text-center"
                />
                <span>{t('serieDetail.generateTerms.recurrence.weeksSuffix')}</span>
              </div>
              <ToggleGroup
                type="multiple"
                variant="outline"
                size="sm"
                value={daysOfWeek.map(String)}
                onValueChange={(values) => onDaysOfWeekChange(values.map(Number))}
                spacing={4}
              >
                {DOW.map((day) => (
                  <ToggleGroupItem
                    key={day.value}
                    value={String(day.value)}
                    className={cn('w-[34px]')}
                  >
                    {t(`serieDetail.generateTerms.dow.${day.labelKey}`)}
                  </ToggleGroupItem>
                ))}
              </ToggleGroup>
            </div>
          ) : null}
        </div>
      </Panel>
    </>
  );
}
